import { useMemo } from "react";
import { useUserData } from "../../../hooks/useUserData";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
  getSessionRoleName,
} from "../../../utils/permissionUtils";
import { organizationHasCapaModule } from "../../../utils/orgModuleAccess";

export { organizationHasCapaModule };

/**
 * Org must be licensed for CAPA (System Administrator included — matches sidebar).
 * Role permissions alone are not enough.
 */
export function canAccessCapaModule({ userData }) {
  return organizationHasCapaModule(userData);
}

/** My CAPA Tasks — available to org users when CAPA is licensed; not a role permission. */
export function canAccessCapaMyWork({ userData }) {
  return organizationHasCapaModule(userData);
}

/**
 * Create CAPA — org CAPA licence + ADD_CAPA (or System Administrator).
 * Use for Study / ISF / Schedule entry points and /capa/new.
 */
export function canCreateCapaRecord({ userData, isSystemAdmin, permissions }) {
  if (!userData || !organizationHasCapaModule(userData)) return false;
  if (isSystemAdmin) return true;
  const perms =
    permissions ??
    (isSystemAdmin
      ? getAllPermissionKeys()
      : parsePermissions(userData?.Permission || ""));
  return hasPermission(perms, "ADD_CAPA");
}

function normalizeStaffId(id) {
  return String(id || "")
    .trim()
    .toLowerCase()
    .replace(/[{}]/g, "");
}

/** Staff id of the user who created the CAPA row. */
export function getCapaCreatorStaffId(capaRow) {
  if (!capaRow) return "";
  return normalizeStaffId(
    capaRow.CreatedByStaffId ??
      capaRow.createdByStaffId ??
      capaRow.CreatedBy ??
      capaRow.createdBy,
  );
}

export function isCapaCreator(capaRow, userData) {
  const creatorId = getCapaCreatorStaffId(capaRow);
  const staffId = normalizeStaffId(userData?.StaffId);
  return creatorId !== "" && creatorId === staffId;
}

/** CAPA owner (workflow driver) — not a role permission. */
export function isCapaOwner(capaRow, userData) {
  if (!capaRow || !userData?.StaffId) return false;
  const ownerId = normalizeStaffId(capaRow.OwnerStaffId ?? capaRow.ownerStaffId);
  const staffId = normalizeStaffId(userData.StaffId);
  return ownerId !== "" && ownerId === staffId;
}

/** Stage transitions — CAPA owner or System Administrator only. */
export function canChangeCapaStage({ capaRow, userData, isSystemAdmin }) {
  if (!organizationHasCapaModule(userData)) return false;
  if (isSystemAdmin) return true;
  if (!capaRow) return false;
  return isCapaOwner(capaRow, userData);
}

/** Assigned staff can act on their task row (no role permission). */
export function isCapaTaskAssignee(taskRow, userData) {
  if (!taskRow || !userData?.StaffId) return false;
  const assigneeId = normalizeStaffId(
    taskRow.OwnerStaffId ??
      taskRow.ownerStaffId ??
      taskRow.ApproverStaffId ??
      taskRow.approverStaffId ??
      taskRow.AssignedStaffId ??
      taskRow.assignedStaffId,
  );
  const staffId = normalizeStaffId(userData.StaffId);
  return assigneeId !== "" && assigneeId === staffId;
}

/** Update/delete — System Administrator or CAPA creator only (not assignable permissions). */
export function canUpdateCapaRecord({ capaRow, userData, isSystemAdmin }) {
  if (!organizationHasCapaModule(userData)) return false;
  if (isSystemAdmin) return true;
  if (!capaRow) return false;
  return isCapaCreator(capaRow, userData);
}

export function canDeleteCapaRecord({ capaRow, userData, isSystemAdmin }) {
  if (!organizationHasCapaModule(userData)) return false;
  if (isSystemAdmin) return true;
  if (!capaRow) return false;
  return isCapaCreator(capaRow, userData);
}

/** Shared CAPA permission checks (align with constants/permissions.js). */
export function useCapaAccess() {
  const { userData } = useUserData();
  const isSystemAdmin = isSystemAdministratorRole(getSessionRoleName(userData));
  const orgHasCapa = organizationHasCapaModule(userData);
  const perms = useMemo(
    () =>
      isSystemAdmin
        ? getAllPermissionKeys()
        : parsePermissions(userData?.Permission || ""),
    [isSystemAdmin, userData?.Permission],
  );

  const can = (key) =>
    orgHasCapa && (isSystemAdmin || hasPermission(perms, key));

  const canViewCapa = can("VIEW_CAPA");
  const canAddCapa = can("ADD_CAPA");
  const canViewRemovedCapa = can("VIEW_REMOVED_CAPA");
  const canCapaReports = can("CAPA_REPORTS");
  const canCapaAuditLog = can("CAPA_AUDIT_LOG");
  const canCapaConfigure = can("CAPA_CONFIGURE");
  const canCapaMyWork = canAccessCapaMyWork({ userData });

  return {
    userData,
    isSystemAdmin,
    orgHasCapa,
    canViewCapa,
    canAddCapa,
    canViewRemovedCapa,
    canCapaReports,
    canCapaAuditLog,
    canCapaMyWork,
    canCapaConfigure,
  };
}
